import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import {
  createGalleryItem,
  getGalleryItems,
  updateGalleryItem,
  deleteGalleryItem
} from "../services/galleryService";

const gallerySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  image_url: z.string().trim().min(1, "Image is required")
});

const defaultValues = {
  name: "",
  image_url: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemPendingDelete, setItemPendingDelete] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const fileInputRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(gallerySchema)
  });

  const imageUrl = watch("image_url");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({
        ...current,
        page: targetPage,
        limit: targetLimit
      }));
      return;
    }

    loadItems(targetPage, targetLimit);
  };

  async function loadItems(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);

    const response = await getGalleryItems({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setItems(response.data.items || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load gallery items");
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadItems(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingItemId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingItemId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(false);
  };

  const openDeleteModal = (item) => {
    setItemPendingDelete(item);
  };

  const closeDeleteModal = () => {
    if (isDeletingItem) {
      return;
    }
    setItemPendingDelete(null);
  };

  const handleFileRead = (file) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploadingImage(true);

    uploadFileToS3(file, "gallery").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        setIsUploadingImage(false);
        return;
      }

      setValue("image_url", response.data.file.url, {
        shouldDirty: true,
        shouldValidate: true
      });
      showSuccessToast("Image uploaded successfully");
      setIsUploadingImage(false);
    });
  };

  const handleFileSelect = (event) => {
    handleFileRead(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFileRead(event.dataTransfer.files?.[0]);
  };

  const handleEdit = (item) => {
    setEditingItemId(item.id);
    reset({
      name: item.name,
      image_url: item.image_url
    });
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!itemPendingDelete) {
      return;
    }

    setIsDeletingItem(true);

    const response = await deleteGalleryItem(itemPendingDelete.id);

    setIsDeletingItem(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete gallery item");
      return;
    }

    const nextPage = items.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Gallery item deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const response = editingItemId
      ? await updateGalleryItem(editingItemId, values)
      : await createGalleryItem(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save gallery item");
      return;
    }

    showSuccessToast(response.message || "Gallery item saved successfully");
    closeModal();

    if (editingItemId) {
      refreshListing(pagination.page, pagination.limit);
      return;
    }

    refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Media Module"
        title="Gallery"
        description="Manage gallery images with upload and drag-and-drop functionality."
        badge={`${pagination.total} Images`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Gallery Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              View and manage your gallery items.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <ImageIcon className="h-5 w-5" />
            </div>
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Image
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading gallery items...
            </div>
          ) : items.length ? (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((item) => (
                  <div key={item.id} className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white transition-all hover:shadow-lg">
                    <div className="aspect-square overflow-hidden bg-slate-100">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="line-clamp-1 text-sm font-semibold text-panel-ink">{item.name}</h3>
                      <div className="mt-4 flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => handleEdit(item)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => openDeleteModal(item)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> items
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => refreshListing(pagination.page - 1, pagination.limit)}
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Prev
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {pageNumbers.map((item, index) =>
                        item === "..." ? (
                          <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={item}
                            type="button"
                            size="sm"
                            variant={pagination.page === item ? "default" : "outline"}
                            onClick={() => refreshListing(item, pagination.limit)}
                          >
                            {item}
                          </Button>
                        )
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => refreshListing(pagination.page + 1, pagination.limit)}
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-12 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <ImageIcon className="h-8 w-8" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No images found</p>
              <p className="mt-2 text-sm text-panel-muted">
                Start building your gallery by uploading your first image.
              </p>
              <Button type="button" className="mt-6" onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add Image
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <h2 className="text-2xl font-bold text-panel-ink">
                  {editingItemId ? "Edit Image" : "Add Image"}
                </h2>
                <p className="mt-1 text-sm text-panel-muted">
                  Provide a name and an image for the gallery.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="p-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <FieldLabel>Name</FieldLabel>
                  <Input placeholder="Enter image name" {...register("name")} />
                  {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                </div>

                <div className="space-y-2">
                   <FieldLabel>Image</FieldLabel>
                   <div
                    className={`relative rounded-3xl border-2 border-dashed p-8 transition-colors ${
                      isDragActive ? "border-primary bg-primary/5" : "border-slate-200 bg-slate-50/70"
                    }`}
                    onDragEnter={(event) => {
                      event.preventDefault();
                      setIsDragActive(true);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsDragActive(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      setIsDragActive(false);
                    }}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />

                    <div className="flex flex-col items-center text-center">
                      {imageUrl ? (
                        <div className="relative">
                          <img
                            src={imageUrl}
                            alt="Gallery preview"
                            className="max-h-64 rounded-2xl object-contain shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setValue("image_url", "", { shouldDirty: true, shouldValidate: true })}
                            className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-rose-600 text-white shadow-lg transition-transform hover:scale-110"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <Upload className="h-8 w-8" />
                          </div>
                          <p className="mt-4 text-base font-semibold text-panel-ink">
                            Click to upload or drag and drop
                          </p>
                          <p className="mt-2 text-sm text-panel-muted">
                            PNG, JPG, GIF up to 10MB
                          </p>
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingImage}
                          >
                            {isUploadingImage ? "Uploading..." : "Select File"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {errors.image_url ? <FieldError>{errors.image_url.message}</FieldError> : null}
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <Button type="submit" disabled={isSubmitting || isUploadingImage} className="flex-1">
                  {isSubmitting ? "Saving..." : editingItemId ? "Update Image" : "Save Image"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {itemPendingDelete ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white p-6 shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-panel-ink">Delete Image</h2>
            <p className="mt-3 text-sm leading-6 text-panel-muted">
              Are you sure you want to delete <span className="font-semibold text-panel-ink">{itemPendingDelete.name}</span>?
              This action cannot be undone.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingItem}>
                {isDeletingItem ? "Deleting..." : "Delete Image"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingItem}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
